/**
 * The gardener's live beds (locations), read once and shared, and the ways to
 * add, change, retire, restore and delete one.
 *
 * A bed change reaches past the bed list: every plant carries its bed's name,
 * and a bed's kind, sun, covers and name shape its jobs, so the writes here
 * invalidate the plants too, and read Grow's advice afresh (Today's brief and
 * the outlook: lib/briefFreshness gardenWritten; D56, a bed's covers
 * taken out went on saying "Your heavy frost cloth is enough" on Today).
 *
 * Reads GET /locations?status=1 (api/locations) under [LOCATIONS_QUERY_KEY]
 * ('locations'), with the app's default five minutes' staleness; other
 * screens that read the beds directly use the same key and so share the
 * answer. Archived beds are not here (ArchivedLocations has its own query).
 *
 * Mounted per route by App around the pages that read the beds; read with
 * useLocationsContext (shared/hooks, or LocationsContextDefinition) by
 * features/locations and the photo tray.
 *
 * @module LocationsContext
 */
import { ReactNode, useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createLocation as apiCreateLocation,
  updateLocation as apiUpdateLocation,
  deleteLocation as apiDeleteLocation,
  setLocationStatus,
  LocationMutationResponse,
  LocationDeleteResponse
} from '../api/locations';
import { LocationsContext } from './LocationsContextDefinition';
import { LOCATIONS_QUERY_KEY, PLANTS_QUERY_KEY } from '@/config/tunables/ui';
import { useBeds } from '@/shared/hooks/useBeds';
import { gardenWritten } from '@/shared/lib/briefFreshness';
import { ARCHIVED_LOCATIONS_QUERY_KEY } from '@/config/tunables/garden';

/**
 * Provides the Locations context to its children components.
 * Manages fetching, caching, and mutations (add, update, delete) for location data
 * using React Query.
 *
 * @param {object} props - The component props.
 * @param {ReactNode} props.children - The child components to wrap with the provider.
 * @returns {JSX.Element} The LocationsProvider component.
 */
export function LocationsProvider({ children }: { children: ReactNode }): JSX.Element {
  /** The app's query client, for the invalidations after a write. */
  const queryClient = useQueryClient();

  /** The live beds, under the shared 'locations' key (see the module note). */
  const { data: locations, isLoading: loading, error, refetch } = useBeds();

  /** Creating a bed; only the bed list changes. */
  const createMutation = useMutation<LocationMutationResponse, Error, FormData>({
    mutationFn: apiCreateLocation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LOCATIONS_QUERY_KEY] });
      // A new bed is on the Beds page's list from the brief (D56), and its kind and covers shape its jobs.
      gardenWritten(queryClient);
    },
  });

  /** Changing a bed: the bed list, and every plant that names it (below). */
  const updateMutation = useMutation<LocationMutationResponse, Error, { locationId: number; data: FormData }>({
    mutationFn: ({ locationId, data }) => apiUpdateLocation(locationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LOCATIONS_QUERY_KEY] });
      /*
       * The garden too, because every plant carries the name of the bed it is
       * in. Renaming "Greenhouse" to "Big greenhouse" used to change it here
       * and nowhere else: every card went on saying Greenhouse until something
       * unrelated happened to refetch the plants.
       */
      queryClient.invalidateQueries({ queryKey: [PLANTS_QUERY_KEY] });
      // Its kind, sun, covers and name shape its jobs: Today, Plants, Weather and Beds read them afresh (D56).
      gardenWritten(queryClient);
    },
  });

  /**
   * Archiving and restoring. Both lists change, and so does the garden: a
   * plant carries its location, and the outlook skips sheltered beds.
   */
  const statusMutation = useMutation<unknown, Error, { locationId: number; status: 0 | 1 }>({
    mutationFn: ({ locationId, status }) => setLocationStatus(locationId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LOCATIONS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ARCHIVED_LOCATIONS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PLANTS_QUERY_KEY] });
      // An archived bed's jobs go, a restored bed's come back (D56).
      gardenWritten(queryClient);
    },
  });
  /*
   * Callbacks below depend on mutateAsync, not on the mutation object: the
   * object is new every render, which made every callback, and so the
   * context value, new every render too. mutateAsync is stable.
   */
  const { mutateAsync: setStatus } = statusMutation;
  /**
   * Retires a bed (status 0). The server refuses while plants still grow in it.
   *
   * @param locationId - The bed.
   */
  const archiveLocation = useCallback(async (locationId: number): Promise<void> => {
    await setStatus({ locationId, status: 0 });
  }, [setStatus]);
  /**
   * Puts a retired bed back (status 1).
   *
   * @param locationId - The bed.
   */
  const restoreLocation = useCallback(async (locationId: number): Promise<void> => {
    await setStatus({ locationId, status: 1 });
  }, [setStatus]);

  /** Deleting a bed: the bed list, and the plants that were in it. */
  const deleteMutation = useMutation<LocationDeleteResponse, Error, number>({
    mutationFn: apiDeleteLocation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LOCATIONS_QUERY_KEY] });
      // Same reason as the rename: the plants that lived there say so.
      queryClient.invalidateQueries({ queryKey: [PLANTS_QUERY_KEY] });
      // Gone from the Beds page's list (D56).
      gardenWritten(queryClient);
    },
  });

  /** Reads the beds again now, whatever their staleness. */
  const refreshLocations = useCallback(async () => {
    await refetch();
  }, [refetch]);

  /** Creates a bed (stable, unlike the mutation object). */
  const { mutateAsync: create } = createMutation;
  /** Changes a bed (stable). */
  const { mutateAsync: update } = updateMutation;
  /** Deletes a bed (stable). */
  const { mutateAsync: remove } = deleteMutation;

  /**
   * Callback function to add a new location using the `createMutation`.
   * @param {FormData} data - The data for the new location.
   * @returns {Promise<LocationMutationResponse>} A promise resolving to the API response.
   */
  const addLocation = useCallback(async (data: FormData): Promise<LocationMutationResponse> => {
    return create(data);
  }, [create]);

  /**
   * Callback function to update an existing location using the `updateMutation`.
   * @param {number} locationId - The ID of the location to update.
   * @param {FormData} data - The updated data for the location.
   * @returns {Promise<LocationMutationResponse>} A promise resolving to the API response.
   */
  const updateLocation = useCallback(async (locationId: number, data: FormData): Promise<LocationMutationResponse> => {
    return update({ locationId, data });
  }, [update]);

  /**
   * Callback function to delete a location using the `deleteMutation`.
   * @param {number} locationId - The ID of the location to delete.
   * @returns {Promise<LocationDeleteResponse>} A promise resolving to the API response.
   */
  const deleteLocation = useCallback(async (locationId: number): Promise<LocationDeleteResponse> => {
    return remove(locationId);
  }, [remove]);

  /**
   * What the context holds. One object per change, not per render, so readers re-render only when
   * the beds or their loading state do.
   */
  const value = useMemo(() => ({
    locations: locations ?? null, // Provide null if data is undefined
    loading,
    error: error ? error : null, // Provide null if no error
    refreshLocations,
    addLocation,
    updateLocation,
    deleteLocation,
    archiveLocation,
    restoreLocation,
  }), [locations, loading, error, refreshLocations, addLocation, updateLocation, deleteLocation, archiveLocation, restoreLocation]);

  return (
    <LocationsContext.Provider value={value}>
      {children}
    </LocationsContext.Provider>
  );
}